import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import { getAdminUsers, updateAdminUser, deleteAdminUser, resetMfaForAdminUser } from './thunks'

const initialState: AdministratorsState = {
  users: [],
  searchResults: [],
  error: null,
  status: 'idle',
  editingAdminUserStatus: 'idle',
  editingAdminUserError: null,
  deletingAdminUserStatus: 'idle',
  deletingAdminUserError: null,
  mfaResetStatus: 'idle',
  mfaResetError: null,
}

const slice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAdminEditStatuses: (state) => {
      state.editingAdminUserStatus = 'idle'
      state.deletingAdminUserStatus = 'idle'
      state.mfaResetStatus = 'idle'
      state.editingAdminUserError = null
      state.deletingAdminUserError = null
      state.mfaResetError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminUsers.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getAdminUsers.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getAdminUsers.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.users = action.payload
      })

      .addCase(updateAdminUser.pending, (state) => {
        state.editingAdminUserStatus = 'loading'
        state.editingAdminUserError = null
        state.deletingAdminUserStatus = 'idle'
        state.mfaResetStatus = 'idle'
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.editingAdminUserStatus = 'failed'
        state.editingAdminUserError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateAdminUser.fulfilled, (state, action) => {
        state.editingAdminUserStatus = 'succeeded'
        const updatedId = action.payload.id
        const currentIndex = state.users.map((user: User) => user.id).indexOf(updatedId)
        state.users[currentIndex] = action.payload
      })

      .addCase(deleteAdminUser.pending, (state) => {
        state.deletingAdminUserStatus = 'loading'
        state.deletingAdminUserError = null
        state.editingAdminUserStatus = 'idle'
        state.mfaResetStatus = 'idle'
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.deletingAdminUserStatus = 'failed'
        state.deletingAdminUserError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.deletingAdminUserStatus = 'succeeded'
        const deletedId = action.meta.arg.id
        const currentIndex = state.users.map((user: User) => user.id).indexOf(deletedId)
        state.users.splice(currentIndex, 1)
      })

      .addCase(resetMfaForAdminUser.pending, (state) => {
        state.mfaResetStatus = 'loading'
        state.mfaResetError = null
        state.editingAdminUserStatus = 'idle'
        state.deletingAdminUserStatus = 'idle'
      })
      .addCase(resetMfaForAdminUser.rejected, (state, action) => {
        state.mfaResetStatus = 'failed'
        state.mfaResetError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(resetMfaForAdminUser.fulfilled, (state, action) => {
        state.mfaResetStatus = 'succeeded'
        const updatedId = action.payload.id
        const currentIndex = state.users.map((user: User) => user.id).indexOf(updatedId)
        state.users[currentIndex] = action.payload
      })
  },
})

export const { clearAdminEditStatuses } = slice.actions

export const selectAdminUsers = (state: RootState): AdministratorsState => state.administrators

export default slice.reducer
